import { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Purchases, CustomerInfo } from '@revenuecat/purchases-js';
import { useGlobalStore } from './GlobalStoreProvider.tsx';
import { REVENUECAT_WEB_BILLING_API_KEY, REVENUECAT_PAYMENT_LINK, REVENUECAT_PRODUCT_LINK } from '../constants/config.ts';
import { Children } from '../constants/types/prop.types.ts';
import { useMyTier } from '../modules/admin/subscriptions/hooks/useSubscriptionsData.ts';
import { ISubscriptionTier } from '../api/services/subscriptions/types.ts';

interface IRevenueCatContext {
  purchases: Purchases | null;
  isInitialized: boolean;
  customerInfo: any;
  refreshCustomerInfo: () => Promise<void>;
  myTier: ISubscriptionTier | null;
  appUserId: string;
  payLink: string;
  paySeatLink: string;
  getFeatureStatus: (feature: WebFeatures) => boolean;
}

export enum WebFeatures {
  HotJobs = 'hot_jobs',
  MatchedShortlists = 'matched_shortlists',
  SubContractorsAvailability = 'sub_contractors_availability',
  AddMemberSeats = 'add_member_seats',
}

export enum ConsumableProducts {
  SeatWeb = 'seat_web',
}

const RevenueCatContext = createContext<IRevenueCatContext>({
  purchases: null,
  isInitialized: false,
  customerInfo: null,
  refreshCustomerInfo: async () => { },
  myTier: null,
  appUserId: '',
  payLink: '',
  paySeatLink: '',
  getFeatureStatus: () => false,
});

export const useRevenueCat = () => {
  const context = useContext(RevenueCatContext);
  if (!context) {
    throw new Error('useRevenueCat must be used within a RevenueCatProvider');
  }
  return context;
};

const RevenueCatProvider: FC<Children> = ({ children }) => {
  const { agency, user } = useGlobalStore();
  const [purchases, setPurchases] = useState<Purchases | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo | null>(null);

  const myTier = useMyTier();

  const getFeatureStatus = useCallback(
    (featureToCheck: WebFeatures) => {
      const featureStatus = myTier.data?.features.some((feature) =>
        Object.values(feature).some((value) => {
          switch (featureToCheck) {
            case WebFeatures.HotJobs:
              return value === WebFeatures.HotJobs;
            case WebFeatures.MatchedShortlists:
              return value === WebFeatures.MatchedShortlists;
            case WebFeatures.SubContractorsAvailability:
              return value === WebFeatures.SubContractorsAvailability;
            case WebFeatures.AddMemberSeats:
              return value === WebFeatures.AddMemberSeats;
            default:
              return false;
          }
        }),
      );

      return featureStatus || false;
    },
    [myTier.data],
  );

  const refreshCustomerInfo = async () => {
    if (purchases) {
      try {
        const info = await purchases.getCustomerInfo();
        setCustomerInfo(info);
      } catch (error) {
        console.error('Error refreshing customer info:', error);
      }
    }
  };

  const appUserId = useMemo(() => `agency-${agency.data?.id?.toString()}`, [agency.data?.id]);
  const payLink = useMemo(() => `${REVENUECAT_PAYMENT_LINK}${appUserId}`, [appUserId]);
  const paySeatLink = useMemo(() => `${REVENUECAT_PRODUCT_LINK}${appUserId}`, [appUserId]);

  useEffect(() => {
    const initRevenueCat = async () => {
      // Only initialize if we have agency data and API key
      if (!agency.data?.id || !REVENUECAT_WEB_BILLING_API_KEY || !user.data?.id) {
        setIsInitialized(false);
        return;
      }

      try {
        // Use agency ID as the user ID for RevenueCat

        const purchasesInstance = Purchases.configure({
          apiKey: REVENUECAT_WEB_BILLING_API_KEY,
          appUserId,
        });

        setPurchases(purchasesInstance);
        setIsInitialized(true);

        // Get initial customer info
        const info = await purchasesInstance.getCustomerInfo();
        setCustomerInfo(info);
        console.log('Customer info:', info);

        console.log('RevenueCat initialized successfully for agency:', appUserId);
      } catch (error) {
        console.error('Error initializing RevenueCat:', error);
        setIsInitialized(false);
      }
    };

    initRevenueCat();
  }, [agency.data?.id, user.data?.id]);

  const value = useMemo(
    () => ({
      purchases,
      isInitialized,
      customerInfo,
      refreshCustomerInfo,
      appUserId,
      payLink,
      paySeatLink,
      getFeatureStatus,
      myTier: myTier.data ?? null,
    }),
    [
      purchases,
      isInitialized,
      customerInfo,
      refreshCustomerInfo,
      appUserId,
      payLink,
      paySeatLink,
      getFeatureStatus,
      myTier,
    ],
  );

  return <RevenueCatContext.Provider value={value}>{children}</RevenueCatContext.Provider>;
};

export default RevenueCatProvider;
